// Advanced settings for one department (admin / superadmin) — like
// "Developer options" on a phone: a warning and a deliberate unlock (type
// the department's name), then a list of advanced modules. Today the list
// has one entry, Attendance policy; later modules add a line to MODULES.
//
// The unlock lasts while this window is open, so going back from a module
// to the list doesn't ask again.
import { useState } from "react";
import type { LucideIcon } from "lucide-react";
import { AlertTriangle, ChevronRight, ClipboardCheck, SlidersHorizontal } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useInstituteLabels } from "@/lib/instituteLabels";
import type { DepartmentProfile } from "./structureApi";
import { AttendancePolicyPanel } from "./AttendancePolicyPanel";

type ModuleKey = "attendance";

const MODULES: { key: ModuleKey; title: string; description: string; icon: LucideIcon }[] = [
    {
        key: "attendance",
        title: "Attendance policy",
        description: "What the attendance form asks for, and how attendance is counted.",
        icon: ClipboardCheck,
    },
];

export function AdvancedSettings({ department, onClose }: { department: DepartmentProfile | null; onClose: () => void }) {
    const L = useInstituteLabels();
    const [ typed, setTyped ] = useState("");
    const [ open, setOpen ] = useState<ModuleKey | null>(null);

    if (!department) return null;
    const deptName = department.displayName === department.departmentName
        ? department.departmentName.replace(/_/g, " ")
        : department.displayName;
    const unlocked = typed.trim().toLowerCase() === deptName.trim().toLowerCase();

    if (open === "attendance") {
        return <AttendancePolicyPanel department={department} onBack={() => setOpen(null)} onClose={onClose} />;
    }

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <SlidersHorizontal className="h-5 w-5 text-primary" /> Advanced settings
                    </DialogTitle>
                    <DialogDescription>{L.department}: {deptName}</DialogDescription>
                </DialogHeader>

                {!unlocked ? (
                    <div className="space-y-3">
                        <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                            <AlertTriangle className="h-5 w-5 shrink-0" />
                            <div className="space-y-1">
                                <p className="font-semibold">Change these at your own risk</p>
                                <p>These settings change how UNILinkd behaves for everyone in this {L.department.toLowerCase()}.
                                    Most institutes never need them: left alone, everything works as it does today.</p>
                            </div>
                        </div>
                        <Label htmlFor="advanced-unlock">Type the {L.department.toLowerCase()} name “{deptName}” to continue</Label>
                        <Input id="advanced-unlock" value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus autoComplete="off" />
                    </div>
                ) : (
                    <ul className="divide-y rounded-lg border">
                        {MODULES.map(m => (
                            <li key={m.key}>
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                                    onClick={() => setOpen(m.key)}
                                >
                                    <span className="rounded-md bg-primary/10 p-2"><m.icon className="h-4 w-4 text-primary" /></span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-semibold">{m.title}</span>
                                        <span className="block text-xs text-muted-foreground">{m.description}</span>
                                    </span>
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </DialogContent>
        </Dialog>
    );
}
